import { DateInput } from "@/components/common/DateInput";
import type { FieldType } from "@/schemas/digital-files";
import { formatNumberInput } from "@/utils/number";
import {
  Button,
  Icon,
  Input,
  Switch,
  Textarea,
} from "@elaniintc/goes-design-system";
import { DataCenterDataSelect } from "../../components/DataCenterDataSelect";
import { FileInput } from "@/components/common/input-file";
import { DateRangeInput } from "./DateRangeField";
import {
  IdentificationField,
  type IdentificationFieldType,
} from "@/components/common/identification-field";
import { getDocumentType } from "../../utils";
import { InputPhone } from "@/components/common/input-phone";
import { DataCenterDataMultiselect } from "@/pages/digital-files/entity-registry/components/DataCenterDataMultiselect";

type FieldRendererArgs = {
  field: FieldType;
  val: unknown;
  index?: number;
  onValChange: (newVal: unknown) => void;
  onBlur: () => void;
};

function renderDatabaseConfig(field: FieldType) {
  const hasDatabase =
    field.configuration?.options_root_table &&
    field.configuration?.options_root_column;
  return hasDatabase
    ? {
        table: field.configuration?.options_root_table,
        column: field.configuration?.options_root_column,
      }
    : undefined;
}

function renderSelectField({
  field,
  val,
  index,
  onValChange,
  onBlur,
}: FieldRendererArgs): React.ReactNode {
  return (
    <DataCenterDataSelect
      key={`${field.id}-${index}`}
      placeholder={field.configuration?.placeholder || "Seleccione una opción"}
      enableSearch
      initOptions={field.configuration?.options || []}
      database={renderDatabaseConfig(field)}
      name={field.name}
      onChange={(selected) => {
        onValChange(selected);
        onBlur();
      }}
      value={
        typeof val === "string"
          ? val
          : (val as { key: string; label: string } | null)
      }
    />
  );
}

function renderMultiselectField({
  field,
  val,
  index,
  onValChange,
  onBlur,
}: FieldRendererArgs): React.ReactNode {
  return (
    <DataCenterDataMultiselect
      key={`${field.id}-${index}`}
      enableSearch
      initOptions={field.configuration?.options || []}
      className="w-full"
      placeholder={field.configuration?.placeholder || "Seleccione una opción"}
      database={renderDatabaseConfig(field)}
      value={(val as Array<{ key: string; label: string }>) || []}
      onChange={(v) => onValChange(v)}
      onBlur={onBlur}
    />
  );
}

function renderUrlField({
  field,
  val,
  onValChange,
  onBlur,
}: FieldRendererArgs): React.ReactNode {
  const allowedExtensions =
    field.configuration?.allowedFileTypes &&
    field.configuration.allowedFileTypes.length > 0
      ? field.configuration.allowedFileTypes
      : ["*"];
  return (
    <FileInput
      value={val as File[] | File | null}
      onChange={(files) => {
        onValChange(files);
        onBlur();
      }}
      allowMultiples={field?.configuration?.allowMultipleFiles || false}
      allowed_mimes={allowedExtensions}
      maxSizeBytes={
        field.configuration?.maxFileSize &&
        typeof field.configuration.maxFileSize === "number"
          ? field.configuration.maxFileSize * 1024 * 1024
          : undefined
      }
    />
  );
}

function renderBooleanField({
  field,
  val,
  index,
  onValChange,
  onBlur,
}: FieldRendererArgs): React.ReactNode {
  const labels = field.field_type_label?.split("/");
  return (
    <div className="flex gap-2 items-center">
      <Switch
        id={`${field.name}-${index}`}
        checked={Boolean(val)}
        onCheckedChange={(checked) => {
          onValChange(checked);
          onBlur();
        }}
      />
      <span className="text-body text-size-body-sm">
        {val ? labels?.[0] || "Sí" : labels?.[1] || "No"}
      </span>
    </div>
  );
}

function renderTextareaField({
  field,
  val,
  onValChange,
  onBlur,
}: FieldRendererArgs): React.ReactNode {
  return (
    <Textarea
      maxLength={
        field?.configuration?.maxLength
          ? Number(field.configuration.maxLength)
          : undefined
      }
      className="bg-neutral-object-accent-silent border-neutral"
      required={field.is_required}
      placeholder={field.configuration?.placeholder || "Ingrese un valor"}
      value={(val as string) || ""}
      onChange={(e) => onValChange(e.target.value)}
      onBlur={onBlur}
    />
  );
}

function renderDateField({
  field,
  val,
  index,
  onValChange,
  onBlur,
}: FieldRendererArgs): React.ReactNode {
  if (field.configuration?.type === "date_range") {
    const rangeValue = val as { start?: string; end?: string } | undefined;
    return (
      <DateRangeInput
        key={`${field.id}-${index}`}
        minDate={field?.configuration?.minDate}
        maxDate={field.configuration?.maxDate}
        includeTime={field.configuration?.includeTime ?? false}
        format24={field.configuration?.format24 ?? false}
        placeholder={field.configuration?.placeholder || "Selecciona una fecha"}
        value={{ start: rangeValue?.start || "", end: rangeValue?.end || "" }}
        onChange={(dateRange) => onValChange(dateRange)}
        onBlur={onBlur}
      />
    );
  }
  return (
    <DateInput
      key={`${field.id}-${index}`}
      minDate={field?.configuration?.minDate}
      maxDate={field.configuration?.maxDate}
      includeTime={field.configuration?.includeTime ?? false}
      format24={field.configuration?.format24 ?? false}
      placeholder={field.configuration?.placeholder || "Seleccione una fecha"}
      value={(val as string) || ""}
      onChange={(date) => {
        onValChange(date);
        onBlur();
      }}
    />
  );
}

function renderNumberField({
  field,
  val,
  onValChange,
  onBlur,
}: FieldRendererArgs): React.ReactNode {
  return (
    <Input
      maxLength={
        field?.configuration?.maxLength
          ? Number(field.configuration.maxLength)
          : undefined
      }
      className="w-full"
      required={field.is_required}
      placeholder={field.configuration?.placeholder || "Ingrese un número"}
      value={(val as string) || ""}
      onChange={(e) => {
        onValChange(formatNumberInput(e.target.value, true, false));
      }}
      onBlur={onBlur}
    />
  );
}

function renderEmailField({
  field,
  val,
  onValChange,
  onBlur,
}: FieldRendererArgs): React.ReactNode {
  return (
    <Input
      maxLength={
        field?.configuration?.maxLength
          ? Number(field.configuration.maxLength)
          : undefined
      }
      className="w-full"
      required={field.is_required}
      placeholder={
        field.configuration?.placeholder || "Ingrese un correo electrónico"
      }
      value={(val as string) || ""}
      onChange={(e) => onValChange(e.target.value)}
      onBlur={onBlur}
    />
  );
}

function renderIdentificationField({
  field,
  val,
  onValChange,
}: FieldRendererArgs): React.ReactNode {
  const identificationValue =
    (val as { documentType?: string; value?: string }) || {};
  const currentDocType =
    identificationValue.documentType ??
    getDocumentType(field.configuration?.documentType);
  const currentDocNumber = identificationValue.value || "";
  return (
    <IdentificationField
      documentTypeConfig={
        field?.configuration?.documentType as IdentificationFieldType
      }
      documentType={currentDocType as IdentificationFieldType}
      documentNumber={currentDocNumber}
      onDocumentTypeChange={(docType) =>
        onValChange({ documentType: docType, value: "" })
      }
      onDocumentNumberChange={(docNumber) =>
        onValChange({ documentType: currentDocType, value: docNumber })
      }
    />
  );
}

function renderPhoneField({
  field,
  val,
  onValChange,
  onBlur,
}: FieldRendererArgs): React.ReactNode {
  return (
    <InputPhone
      className="w-full"
      required={field.is_required}
      placeholder={
        field.configuration?.placeholder || "Ingrese un número de teléfono"
      }
      value={(val as string) || ""}
      onChange={(e) => onValChange(e.target.value)}
      onBlur={onBlur}
    />
  );
}

function renderDefaultField({
  field,
  val,
  onValChange,
  onBlur,
}: FieldRendererArgs): React.ReactNode {
  return (
    <Input
      maxLength={
        field?.configuration?.maxLength
          ? Number(field.configuration.maxLength)
          : undefined
      }
      className="w-full"
      required={field.is_required}
      placeholder={field.configuration?.placeholder || "Ingrese un valor"}
      value={(val as string) || ""}
      onChange={(e) => onValChange(e.target.value)}
      onBlur={onBlur}
    />
  );
}

const FIELD_RENDERERS: Record<
  string,
  (args: FieldRendererArgs) => React.ReactNode
> = {
  SELECT: renderSelectField,
  MULTISELECT: renderMultiselectField,
  URL: renderUrlField,
  BOOLEAN: renderBooleanField,
  TEXTAREA: renderTextareaField,
  DATE: renderDateField,
  DATETIME: renderDateField,
  NUMBER: renderNumberField,
  EMAIL: renderEmailField,
  PHONE: renderPhoneField,
};

function resolveFieldRenderer(field: FieldType) {
  if (
    field.field_type === "TEXT" &&
    field.configuration?.type === "identification"
  ) {
    return renderIdentificationField;
  }
  return FIELD_RENDERERS[field.field_type] ?? renderDefaultField;
}

export const getFieldByType = (
  field: FieldType,
  value: unknown,
  onChange: (value: unknown) => void,
  onBlur: () => void,
): React.ReactNode => {
  const isArray = field.is_array || false;
  const maxItems = field.max_items ? Number(field.max_items) : Infinity;
  const renderer = resolveFieldRenderer(field);

  const renderFieldInput = (
    val: unknown,
    onValChange: (newVal: unknown) => void,
    index?: number,
    onRemove?: () => void,
    stillCanRemove?: boolean,
  ) => {
    const inputNode = renderer({ field, val, index, onValChange, onBlur });

    if (isArray && onRemove && stillCanRemove) {
      return (
        <div key={index} className="flex gap-2 items-start w-full">
          <div className="flex-1">{inputNode}</div>
          <Button
            aria-label="Eliminar"
            type="button"
            variant="ghost"
            size="icon"
            onClick={onRemove}
            className="mt-1 text-body-loud"
          >
            <Icon type="far" name="times" />
          </Button>
        </div>
      );
    }

    return inputNode;
  };

  if (isArray) {
    const values = Array.isArray(value) ? value : [value || ""];
    return (
      <div className="flex flex-col gap-3 w-full">
        {values.map((v, idx) =>
          renderFieldInput(
            v,
            (newVal) => {
              const newValues = [...values];
              newValues[idx] = newVal;
              onChange(newValues);
            },
            idx,
            () => onChange(values.filter((_, i) => i !== idx)),
            values.length > 1,
          ),
        )}
        {values.length < maxItems && (
          <Button
            variant="quiet"
            type="button"
            className="w-fit text-body-loud text-base leading-7 font-semibold"
            onClick={() => onChange([...values, ""])}
          >
            Añadir campo
            <Icon type="fas" name="plus" className="mr-2" />
          </Button>
        )}
      </div>
    );
  }

  return renderFieldInput(value, onChange);
};
